import { useMemo, useState } from 'react';
import Button from '@/components/Button/newButtons';
import {
	PromptTypeEnum,
	SandboxPromptSchemaProps
} from '@/schemas/sandboxPrompts/schema';
import { ErrorMessage } from '@/components/atoms/ErrorMessage';
import { Select } from '@/components/atoms/Select';
import { Tooltip } from '@/components/atoms/Tooltip';
import { useToast } from '@/components/atoms/Toast';
import { useAiPromptFormValidation } from '../hooks/useAIPromptFormValidation';
import { useUpdateSandboxPromptMutation } from '../hooks/useUpdateSandboxPromptMutation';
import { useCreateSandboxPromptMutation } from '../hooks/useCreateSandboxPromptMutation';
import { useSandboxPromptsListQuery } from '../hooks/useSandboxPromptsListQuery';
import { getSelected } from '../utils/getSelected';

type SavePromptModalProps = Pick<
	SandboxPromptSchemaProps,
	'systemPrompt' | 'userPrompt'
> & {
	isOpen: boolean;
	onClose: () => void;
	isApplied: boolean;
};

type PromptModesType = 'new' | 'existing';

export const SavePromptModal = ({
	isOpen,
	onClose,
	systemPrompt,
	userPrompt,
	isApplied
}: SavePromptModalProps) => {
	const [mode, setMode] = useState<PromptModesType>('new');
	const { data: aiPromptData } = useSandboxPromptsListQuery();
	const [selectedPrompt, setSelectedPrompt] =
		useState<SandboxPromptSchemaProps['name']>('');

	const { toast } = useToast();

	const handlePromptSuccessMutation = () => {
		reset();
		onClose();
		toast({
			title: `Prompt ${isApplied ? 'saved and applied' : 'saved'} successfully.`,
			variant: 'success'
		});
	};

	const { mutate: createMutation } = useCreateSandboxPromptMutation({
		onSuccess: handlePromptSuccessMutation
	});
	const { mutate: updateMutation } = useUpdateSandboxPromptMutation({
		onSuccess: handlePromptSuccessMutation
	});

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
		setError
	} = useAiPromptFormValidation({
		defaultValues: {
			name: '',
			type: undefined,
			userPrompt,
			systemPrompt,
			isActive: isApplied,
			isDefault: false
		}
	});

	const onSubmit = async (data: SandboxPromptSchemaProps) => {
		const isEditing = mode === 'existing';

		const allPrompts = aiPromptData?.data?.data || [];

		const isDuplicate = allPrompts.some(
			prompt =>
				prompt.name === data.name &&
				(!isEditing || prompt.id !== data.id) // allow same name if it's the one being edited
		);

		if (isDuplicate) {
			setError('name', {
				type: 'manual',
				message: 'A prompt with this name already exists.'
			});
			return;
		}

		const payload = {
			...data,
			systemPrompt,
			userPrompt,
			isActive: isApplied
		};

		if (isEditing && data?.id) {
			return updateMutation({
				promptId: data?.id,
				data: payload
			});
		}
		return createMutation(payload);
	};

	const handleSelectValue = (
		key: keyof SandboxPromptSchemaProps,
		value: SandboxPromptSchemaProps[keyof SandboxPromptSchemaProps]
	) => {
		setValue(key, value);
	};

	const handleModeChange = (newMode: 'new' | 'existing') => {
		setMode(newMode);
		setSelectedPrompt('');
		if (newMode === 'existing') return;

		reset({
			name: '',
			type: undefined,
			userPrompt,
			systemPrompt,
			isActive: isApplied,
			isDefault: false
		});
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[50] flex items-center justify-center bg-black bg-opacity-60">
			<div className="z-100 w-full max-w-sm rounded-lg border border-dm-grey bg-dm-black1 p-6 shadow-lg">
				<h2 className="mb-4 text-xl font-semibold text-dm-white">
					Save AI Prompt
				</h2>

				<form
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col gap-4"
				>
					{/* Mode toggle */}
					<div className="flex gap-4 text-sm text-dm-white">
						<label className="flex cursor-pointer items-center gap-2">
							<input
								type="radio"
								value="new"
								checked={mode === 'new'}
								onChange={() => handleModeChange('new')}
								className="peer hidden"
							/>
							<span className="h-4 w-4 rounded-full border border-dm-grey peer-checked:border-dm-white peer-checked:bg-dm-white" />
							New
						</label>

						<label className="flex cursor-pointer items-center gap-2">
							<input
								type="radio"
								value="existing"
								checked={mode === 'existing'}
								onChange={() => handleModeChange('existing')}
								className="peer hidden"
							/>
							<span className="h-4 w-4 rounded-full border border-dm-grey peer-checked:border-dm-white peer-checked:bg-dm-white" />
							Overwrite Existing
						</label>
					</div>

					{/* Inputs */}
					<SelectPromptOptions
						mode={mode}
						onSelectPrompt={handleSelectValue}
						errorMessage={errors.type?.message}
						selectedPrompt={selectedPrompt}
						setSelectedPrompt={setSelectedPrompt}
					/>

					{mode === 'new' && (
						<>
							<input
								{...register('name')}
								placeholder="Enter prompt name"
								className="bg-dm-black2 mb-2 w-full rounded-md border border-dm-grey px-3 py-2 text-dm-white placeholder:text-dm-grey focus:outline-none"
							/>

							<ErrorMessage message={errors.name?.message} />
						</>
					)}

					{/* Buttons */}
					<div className="mt-4 flex justify-between gap-2">
						<Button variant="secondary" red onClick={onClose}>
							Cancel
						</Button>

						<Button type="submit">
							{isApplied ? 'Save & Apply' : 'Save'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

type SelectPromptOptionsProps = {
	mode: PromptModesType;
	errorMessage?: string;
	selectedPrompt: string;
	setSelectedPrompt: (val: string) => void;
	onSelectPrompt: (
		key: keyof SandboxPromptSchemaProps,
		value: SandboxPromptSchemaProps[keyof SandboxPromptSchemaProps]
	) => void;
};

const SelectPromptOptions = ({
	mode,
	errorMessage,
	onSelectPrompt,
	selectedPrompt,
	setSelectedPrompt
}: SelectPromptOptionsProps) => {
	const { data: aiPromptData } = useSandboxPromptsListQuery();

	const formatLabel = (value: string) =>
		value
			.replace(/-/g, ' ') // Replace hyphens with spaces
			.replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word

	const AI_OPTIONS = useMemo(
		() =>
			Object.entries(PromptTypeEnum).map(([key, value]) => ({
				id: value as string,
				value: value as string,
				label: formatLabel(key)
			})),
		[]
	);

	const ALL_PROMPTS = useMemo(
		() =>
			aiPromptData?.data?.data
				?.filter(
					(prompt: SandboxPromptSchemaProps) => !prompt.isDefault
				)
				.map((prompt: SandboxPromptSchemaProps) => ({
					id: prompt?.id,
					value: prompt.name,
					label: prompt.name
				})) || [],
		[aiPromptData]
	);

	const handleSelectExistingPrompt = (
		value: SandboxPromptSchemaProps['name']
	) => {
		const foundPrompt = getSelected({
			value,
			optionsArray: aiPromptData?.data?.data || []
		});

		if (!foundPrompt) return;

		setSelectedPrompt(foundPrompt.name);

		Object.entries(foundPrompt).forEach(([key, promptValue]) => {
			onSelectPrompt(key as keyof SandboxPromptSchemaProps, promptValue);
		});
	};

	const handleSelectNewPrompt = (value: string) => {
		setSelectedPrompt(value);
		const selectedType = value.toLowerCase().replace(' ', '-');
		onSelectPrompt(
			'type',
			selectedType as SandboxPromptSchemaProps['type']
		);
	};

	const modeItemOptions: Record<
		PromptModesType,
		{ id?: string; value: string; label: string }[]
	> = {
		new: AI_OPTIONS,
		existing: ALL_PROMPTS
	};

	const modeItemsSelect: Record<PromptModesType, (value: string) => void> = {
		new: handleSelectNewPrompt,
		existing: handleSelectExistingPrompt
	};

	return (
		<>
			<Select.Root
				value={selectedPrompt}
				onValueChange={modeItemsSelect[mode]}
			>
				<Select.Trigger className="w-80">
					<Select.Value
						placeholder={
							mode === 'new'
								? 'Select a prompt type'
								: 'Select prompt to overwrite'
						}
					/>
				</Select.Trigger>

				<Select.Content className="w-80">
					{modeItemOptions[mode].map(({ id, value }) => (
						<Select.Item key={id} value={value}>
							<Tooltip.Provider>
								<Tooltip.Root>
									<Tooltip.Trigger>{value}</Tooltip.Trigger>
									<Tooltip.Content>{value}</Tooltip.Content>
								</Tooltip.Root>
							</Tooltip.Provider>
						</Select.Item>
					))}
				</Select.Content>
			</Select.Root>

			<ErrorMessage message={errorMessage} />
		</>
	);
};
